/**
 * Subscriptions API
 * Handles subscription plans, subscriptions, and benefits
 * Endpoints: /api/subscriptions/*
 */
import apiClient, { createAuthConfig, isMockApi } from "../client";
import {
  mockGetPlans,
  mockGetPlanPackages,
  mockSubscribe,
  mockCancelSubscription,
  mockGetStatus,
  mockGetSubscriptionHistory,
  mockGetBillingHistory,
  mockGetBenefits,
} from "../mocks";

/**
 * Get available subscription plans
 * @returns {Promise<Object>} - { success, plans, total }
 */
export const getPlans = async () => {
  if (isMockApi) return mockGetPlans();
  const response = await apiClient.get('/api/subscriptions/plans');
  return response.data;
};

/**
 * Get packages for a specific subscription plan
 * @param {number} subscriptionId - Subscription plan ID
 * @returns {Promise<Object>} - { success, packages, subscription_id, total }
 */
export const getPlanPackages = async (subscriptionId) => {
  if (isMockApi) return mockGetPlanPackages();
  const response = await apiClient.get(`/api/subscriptions/plans/${subscriptionId}/packages`);
  return response.data;
};

/**
 * Subscribe to a subscription plan
 * @param {string} token - Clerk session token
 * @param {number} packageId - Subscription package ID (package_id from /plans)
 * @returns {Promise<Object>} - Subscription response
 */
export const subscribe = async (token, packageId) => {
  if (isMockApi) return mockSubscribe();
  const response = await apiClient.post(
    '/api/subscriptions/subscribe',
    { package_id: packageId },
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Cancel user's active subscription
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>}
 */
export const cancelSubscription = async (token) => {
  if (isMockApi) return mockCancelSubscription();
  const response = await apiClient.post('/api/subscriptions/cancel', {}, createAuthConfig(token));
  return response.data;
};

/**
 * Get user's subscription status
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, subscription, benefits }
 */
export const getStatus = async (token) => {
  if (isMockApi) return mockGetStatus();
  const response = await apiClient.get('/api/subscriptions/status', createAuthConfig(token));
  return response.data;
};

/**
 * Get user's subscription history
 * @param {string} token - Clerk session token
 * @param {number} limit - Maximum number of history entries
 * @returns {Promise<Object>} - { success, history, total }
 */
export const getHistory = async (token, limit = 50) => {
  if (isMockApi) return mockGetSubscriptionHistory();
  const params = new URLSearchParams({ limit: limit.toString() });
  const response = await apiClient.get(`/api/subscriptions/history?${params}`, createAuthConfig(token));
  return response.data;
};

/**
 * Get combined billing history (credit purchases + subscriptions)
 * @param {string} token - Clerk session token
 * @param {number} limit - Maximum number of entries
 * @returns {Promise<Object>} - { success, history, total }
 */
export const getBillingHistory = async (token, limit = 50) => {
  if (isMockApi) return mockGetBillingHistory();
  const params = new URLSearchParams({ limit: String(limit) });
  const response = await apiClient.get(
    `/api/subscriptions/billing-history?${params}`,
    createAuthConfig(token)
  );
  return response.data;
};

/**
 * Get user's subscription benefits
 * @param {string} token - Clerk session token
 * @returns {Promise<Object>} - { success, benefits }
 */
export const getBenefits = async (token) => {
  if (isMockApi) return mockGetBenefits();
  const response = await apiClient.get('/api/subscriptions/benefits', createAuthConfig(token));
  return response.data;
};

export default {
  getPlans,
  getPlanPackages,
  subscribe,
  cancelSubscription,
  getStatus,
  getHistory,
  getBillingHistory,
  getBenefits,
};





